import { useQuery } from '@tanstack/react-query';
import { memo } from 'react';

import { useRerenderOnInterval } from '@doger/utils/hooks/use-rerender-on-interval';

import { $$privateApi } from '@doger/admin/shared/api';

import {
  LastUpdatedSkeleton,
  MetricCard,
  MetricsSkeleton,
  SystemHealthIndicator,
} from './components';
import { MetricsDomain } from './domain';
import styles from './metrics.module.scss';

const MetricsPage = memo(() => {
  const { data, isLoading, error, isRefetching } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => $$privateApi.getAdminDashboard(),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  if (isLoading) {
    return <MetricsSkeleton />;
  }

  if (error || !data) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>System Metrics</h1>
        </div>
        <div className={styles.error}>
          {error?.message ?? 'Failed to load metrics data'}
        </div>
      </div>
    );
  }

  const healthStatus = MetricsDomain.getHealthStatus(data);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>System Metrics</h1>
        <div className={styles.headerInfo}>
          {isRefetching ? (
            <LastUpdatedSkeleton />
          ) : (
            <div className={styles.lastUpdated}>
              Last updated: {new Date(data.timestamp).toLocaleString()}
            </div>
          )}
        </div>
      </div>

      <div className={styles.healthStatus}>
        <SystemHealthIndicator status={data.healthStatus} />
      </div>

      <div className={styles.metricsGrid}>
        <div className={styles.metricsSection}>
          <h2 className={styles.sectionTitle}>System Performance</h2>
          <div className={styles.cardsGrid}>
            <MetricCard
              title='CPU Usage'
              value={`${data.systemMetrics.cpuUsage.toFixed(1)}%`}
              status={healthStatus.system.cpu}
              description='Current CPU utilization'
            />
            <MetricCard
              title='Memory Usage'
              value={`${data.systemMetrics.memoryUsagePercentage.toFixed(1)}%`}
              status={healthStatus.system.memory}
              description={`${data.systemMetrics.memoryUsage.toFixed(0)} MB / ${data.systemMetrics.totalMemory.toFixed(0)} MB`}
            />
            <UptimeMetricCard uptimeIsoString={data.systemMetrics.uptime} />
          </div>
        </div>

        <div className={styles.metricsSection}>
          <h2 className={styles.sectionTitle}>Response Times</h2>
          <div className={styles.cardsGrid}>
            <MetricCard
              title='Mean Response Time'
              value={`${data.responseTimeMetrics.mean.toFixed(0)}ms`}
              status={healthStatus.responseTime.mean}
              description='Trimmed mean response time'
            />
            <MetricCard
              title='95th Percentile'
              value={`${data.responseTimeMetrics.p95.toFixed(0)}ms`}
              status={healthStatus.responseTime.p95}
              description='95th percentile response time'
            />
            <MetricCard
              title='99th Percentile'
              value={`${data.responseTimeMetrics.p99.toFixed(0)}ms`}
              status={healthStatus.responseTime.p99}
              description='99th percentile response time'
            />
            <MetricCard
              title='Total Requests'
              value={data.responseTimeMetrics.totalRequests.toLocaleString()}
              status={healthStatus.responseTime.totalRequests}
              description='Total requests processed'
            />
          </div>
        </div>

        <div className={styles.metricsSection}>
          <h2 className={styles.sectionTitle}>Error Rates</h2>
          <div className={styles.cardsGrid}>
            <MetricCard
              title='Error Rate'
              value={`${data.errorRateMetrics.errorRate.toFixed(2)}%`}
              status={healthStatus.errorRate.errorRate}
              description='Percentage of failed requests'
            />
            <MetricCard
              title='Success Rate'
              value={`${data.errorRateMetrics.successRate.toFixed(2)}%`}
              status={healthStatus.errorRate.successRate}
              description='Percentage of successful requests'
            />
            <MetricCard
              title='Failed Requests'
              value={data.errorRateMetrics.failedRequests.toLocaleString()}
              status={healthStatus.errorRate.failedRequests}
              description='Total failed requests'
            />
            <MetricCard
              title='Successful Requests'
              value={data.errorRateMetrics.successfulRequests.toLocaleString()}
              status={healthStatus.errorRate.successfulRequests}
              description='Total successful requests'
            />
          </div>
        </div>

        <div className={styles.metricsSection}>
          <h2 className={styles.sectionTitle}>Traffic</h2>
          <div className={styles.cardsGrid}>
            <MetricCard
              title='Current RPS'
              value={data.requestsPerSecond.currentRps.toFixed(1)}
              status={healthStatus.requestsPerSecond.currentRps}
              description='Current requests per second'
            />
            <MetricCard
              title='Average RPS'
              value={data.requestsPerSecond.averageRps.toFixed(1)}
              status={healthStatus.requestsPerSecond.averageRps}
              description='Average RPS over last minute'
            />
            <MetricCard
              title='Peak RPS'
              value={data.requestsPerSecond.peakRps.toFixed(1)}
              status={healthStatus.requestsPerSecond.peakRps}
              description='Peak RPS in last hour'
            />
            <MetricCard
              title='Last Minute Requests'
              value={data.requestsPerSecond.totalRequestsLastMinute.toLocaleString()}
              status={healthStatus.requestsPerSecond.totalRequestsLastMinute}
              description='Total requests in last minute'
            />
          </div>
        </div>
      </div>
    </div>
  );
});

MetricsPage.displayName = 'MetricsPage';

const UptimeMetricCard = memo(
  ({ uptimeIsoString }: { uptimeIsoString: string }) => {
    useRerenderOnInterval(1);

    return (
      <MetricCard
        title='System Uptime'
        value={formatUptime(uptimeIsoString)}
        status='healthy'
        description='Time since last system restart'
      />
    );
  }
);

const formatUptime = (uptimeIsoString: string): string => {
  const startTime = new Date(uptimeIsoString);
  const now = new Date();
  const diffMs = now.getTime() - startTime.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);

  const days = Math.floor(diffSeconds / 86400);
  const hours = Math.floor((diffSeconds % 86400) / 3600);
  const minutes = Math.floor((diffSeconds % 3600) / 60);
  const seconds = Math.floor(diffSeconds % 60);

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }

  return `${seconds}s`;
};

UptimeMetricCard.displayName = 'UptimeMetricCard';
export { MetricsPage };
